import { useRouter } from 'next/router';
import { useMemo } from 'react';

import type { IClientMicrocreditListItem } from '@src/@types/clienttypes';
import type { AffSub4 } from '@src/@types/microcredits';
import { usePageContext } from '@src/config';
import { PIXELS_TYPES } from '@src/constants/analytics';
import { getOrganizationPageLink } from '@src/helpers/links';
import { usePixel } from '@src/hooks/usePixel';
import { HAS_OFFER_SOURCE } from 'entities/Analytics';
import { getPositionFromUrl } from 'widgets/LightweightCard/lib/getPositionFromUrl';

interface IParams {
  offer: IClientMicrocreditListItem;
  position: number;
}

export const useCardClick = ({ offer, position }: IParams) => {
  const { source = HAS_OFFER_SOURCE } = usePageContext();
  const router = useRouter();
  const sourceFromUrlParams = getPositionFromUrl(router);

  const { name, organization, advertising, _id } = offer;
  const { monetization, advSub, productType, buttonColor } = advertising || {};

  const affSub4: AffSub4 = {
    organizationName: organization.name,
    productName: name,
    organizationId: organization._id,
    productId: _id,
  };

  const { pixelLink: monetizationLink } = usePixel(monetization?.pixels.click, {
    sub: advSub,
    type: productType,
    source: sourceFromUrlParams || `${source}|position_${position}`,
    pixelType: PIXELS_TYPES.CLICK,
    affSub4,
  });

  const cardLink = useMemo(() => {
    if (buttonColor !== 'white' && monetizationLink) {
      return { href: monetizationLink, withAdvertising: true };
    }

    return { href: getOrganizationPageLink(organization.alias), withAdvertising: false };
  }, [buttonColor, monetizationLink, organization.alias]);

  return {
    cardLink,
    sourceLink: source,
    pixelDisplayLink: offer?.advertising?.monetization?.pixels?.display,
    sourceFromUrlParams,
  };
};
